import { orderTypeConstants } from "constants/order";
import { UserRole } from "constants/userRole";
import { useUserWithAccount } from "context/UserAccountProvider";
import { parsePrice, centsToDollars, dollarsToCents } from "helpers/price";
import { sizer } from "layout/styles/styled/sizer";
import { Label } from "layout/typography/Label";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { userSelector } from "store/slices/user";
import styled from "styled-components";
import { IOrder } from "types/Order/Order";

interface ITransfer {
  selectedPartyToTransferFrom: {
    name: string;
    id: string;
    totalAmount: number;
    type: string;
  } | null;
  selectedPartyToTransferTo: {
    name: string;
    id: string;
    totalAmount: number;
    type: string;
  } | null;
  selectedAmountToTransfer: number;
  withholdClearFee?: boolean;
}
export const StyledButton = styled(Button)`
  gap: ${sizer(2)};
  margin-bottom: ${sizer(4)};

  span:first-child {
    font-size: ${sizer(3)};
  }
`;

export function SingleTransferForm({
  order,
  transfer,
  setTransfers,
  index,
  handleRemoveTransfer,
  showRemoveButton,
}: {
  order: IOrder;
  transfer: ITransfer;
  setTransfers: any;
  index: number;
  showRemoveButton: boolean;
  handleRemoveTransfer: (index: number) => void;
}) {
  const { accounts } = useUserWithAccount();
  const user = useSelector(userSelector);
  const isSuperAdmin = user.scope === UserRole.SUPER_ADMIN;

  const paymentBreakdownDetails = useMemo(() => {
    return order?.paymentFullBreakdown?.bundledProviderAmounts;
  }, [order]);
  const paymentPlanPayments = useMemo(() => {
    return order?.paymentPlanBreakdown?.payments;
  }, [order]);
  const providerStripeTransfers = useMemo(() => {
    return order?.providerStripeTransfers;
  }, [order]);

  const providerFinixTransfers = useMemo(() => {
    return order?.providerFinixTransfers;
  }, [order]);

  const providerTotals = useMemo(() => {
    const totals: {
      [key: string]: {
        name: string;
        id: string;
        totalAmount: number;
        type: string;
      };
    } = {};
    // Aggregate from Stripe transfers
    providerStripeTransfers?.forEach((transfer) => {
      if (!totals[transfer.providerId]) {
        totals[transfer.providerId] = {
          name: transfer.provider.name,
          id: transfer.providerId,
          totalAmount: 0,
          type: "provider",
        };
      }
      totals[transfer.providerId].totalAmount +=
        transfer.amountInCents + transfer.clearFeeAmountInCents;
    });
    // Aggregate from Finix transfers
    providerFinixTransfers?.forEach((transfer: any) => {
      if (!totals[transfer.providerId]) {
        totals[transfer.providerId] = {
          name: transfer.provider.name,
          id: transfer.providerId,
          totalAmount: 0,
          type: "provider",
        };
      }
      totals[transfer.providerId].totalAmount +=
        transfer.amountInCents + (transfer.clearFeeAmountInCents || 0);
    });
    return Object.values(totals);
  }, [providerStripeTransfers, providerFinixTransfers]);

  const eligibleToProviders = useMemo(() => {
    const providersInOrder: {
      [key: string]: { name: string; id: string; type: string };
    } = {};
    const otherProvidersInAccount: {
      [key: string]: { name: string; id: string; type: string };
    } = {};
    if (paymentPlanPayments) {
      // for payment plans, only allow refunds of payments that have already been transferred to providers
      paymentPlanPayments?.forEach((payment) => {
        if (payment.transferredToProviders) {
          payment.providerBreakdown.forEach((amount: any) => {
            if (!providersInOrder[amount.providerId]) {
              providersInOrder[amount.providerId] = {
                name: amount.providerName,
                id: amount.providerId,
                type: "provider",
              };
            }
          });
        }
      });
    } else {
      // no payment plan
      paymentBreakdownDetails?.forEach((amount) => {
        if (!providersInOrder[amount.providerId]) {
          providersInOrder[amount.providerId] = {
            name: amount.providerName,
            id: amount.providerId,
            type: "provider",
          };
        }
      });
    }
    const accountWithProviders = accounts?.data?.find(
      (account) => account.id === order.accountId
    );
    if (accountWithProviders?.providers) {
      [...accountWithProviders.providers]
        .filter(
          (provider) =>
            !!provider.stripeAccountNumber || !!provider.finixMerchantId
        )
        .sort((a, b) => a.name.localeCompare(b.name))
        .forEach((provider) => {
          if (!otherProvidersInAccount[provider.id]) {
            otherProvidersInAccount[provider.id] = {
              name: provider.name,
              id: provider.id,
              type: "provider",
            };
          }
        });
    }

    return {
      providersInOrder: Object.values(providersInOrder),
      otherProvidersInAccount: Object.values(otherProvidersInAccount),
    };
  }, [accounts, order, paymentPlanPayments, paymentBreakdownDetails]);

  const transferFromOptions = useMemo(() => {
    const fromOptions = [
      ...providerTotals,
      { name: "Clear Health", id: null, totalAmount: null, type: "provider" },
    ];
    return fromOptions;
  }, [providerTotals, order]);

  const transferToOptions = useMemo(() => {
    // Create grouped options structure with empty array fallbacks
    const providersInOrder = eligibleToProviders?.providersInOrder || [];
    const otherProvidersInAccount =
      eligibleToProviders?.otherProvidersInAccount || [];

    // Check if any payment on the order was made by check
    const hasCheckPayment = order.payments?.some(
      (payment) => payment.type === "Check"
    );

    // CLEAR_TO_PATIENT is disabled for Finix accounts. Under the new APPLICATION_FEE
    // model the ClearHealth merchant is no longer a destination in split_transfers, so a
    // reversal naming ClearHealth fails Finix validation. When the "from" party is Clear
    // Health AND the order is on a Finix account, hide the patient option in "to".
    const useFinix = Boolean(order.account?.features?.useFinix);
    const fromIsClearHealth = transfer.selectedPartyToTransferFrom?.id === null;
    const hideClearToPatient = useFinix && fromIsClearHealth;

    // Build patient and Clear Health items, excluding patient if payment was by check
    // OR when CLEAR_TO_PATIENT is disabled (Finix accounts with from=Clear Health)
    const patientAndClearHealthItems = [
      ...(!hasCheckPayment && !hideClearToPatient
        ? [
            {
              name: `${order.patient.firstName} ${order.patient.lastName}`,
              id: order.patientId,
              type: "patient",
            },
          ]
        : []),
      { name: "Clear Health", id: null, type: "provider" },
    ];

    // Everything must be in groups for consistency
    return [
      {
        label: "Patient and Clear Health",
        items: patientAndClearHealthItems,
        type: "group",
      },
      ...(providersInOrder.length > 0
        ? [
            {
              label: "Providers in this order",
              items: providersInOrder,
              type: "group",
            },
          ]
        : []),
      ...(otherProvidersInAccount.length > 0
        ? [
            {
              label: "Other providers in account",
              items: otherProvidersInAccount,
              type: "group",
            },
          ]
        : []),
    ];
  }, [order, eligibleToProviders, transfer.selectedPartyToTransferFrom]);

  const itemTemplate = (item: any) => {
    if (item.type === "group") {
      return null;
    }
    return (
      <div>
        {item.name}{" "}
        {item.totalAmount ? parsePrice(centsToDollars(item.totalAmount)) : ""}
      </div>
    );
  };

  const optionGroupTemplate = (option: any) => {
    return (
      <div className="flex align-items-center text-primary">{option.label}</div>
    );
  };

  const valueTemplate = (option: any, props: any) => {
    return option ? (
      <div>
        {option.name}{" "}
        {option.totalAmount
          ? parsePrice(centsToDollars(option.totalAmount))
          : ""}
      </div>
    ) : (
      // eslint-disable-next-line react/prop-types
      <div>{props.placeholder}</div>
    );
  };

  const onTransferFromChange = (selectedParty: any) => {
    const isClearHealth = selectedParty.id === null;
    const totalAllowable = selectedParty?.totalAmount;
    const currentAmount = transfer.selectedAmountToTransfer;
    if (isClearHealth) {
      // If switching from a provider to Clear Health on a Finix account where the user
      // had previously selected the patient as "to", clear the now-invalid "to" selection.
      const useFinix = Boolean(order.account?.features?.useFinix);
      const toIsPatient =
        transfer.selectedPartyToTransferTo?.type === "patient";
      const shouldClearTo = useFinix && toIsPatient;
      setTransfers((prevTransfers: any) => {
        const updatedTransfers = [...prevTransfers];
        updatedTransfers[index] = {
          ...updatedTransfers[index],
          selectedPartyToTransferFrom: selectedParty,
          ...(shouldClearTo ? { selectedPartyToTransferTo: null } : {}),
        };
        return updatedTransfers;
      });
      return;
    }
    const updatedAmount =
      currentAmount > totalAllowable ? totalAllowable : currentAmount;
    setTransfers((prevTransfers: any) => {
      const updatedTransfers = [...prevTransfers];
      updatedTransfers[index] = {
        ...updatedTransfers[index],
        selectedPartyToTransferFrom: selectedParty,
        selectedAmountToTransfer: updatedAmount,
      };
      return updatedTransfers;
    });
  };

  const onTransferToChange = (selectedParty: any) => {
    setTransfers((prevTransfers: any) => {
      const updatedTransfers = [...prevTransfers];
      updatedTransfers[index] = {
        ...updatedTransfers[index],
        selectedPartyToTransferTo: selectedParty,
      };
      return updatedTransfers;
    });
  };
  const onAmountChange = (amount: number | null) => {
    let newAmount = amount || 0;
    const isClearHealth = transfer?.selectedPartyToTransferFrom?.id === null;
    const totalAllowable = transfer?.selectedPartyToTransferFrom?.totalAmount;
    if (
      !isClearHealth &&
      amount &&
      totalAllowable &&
      dollarsToCents(amount) > totalAllowable
    ) {
      newAmount = centsToDollars(totalAllowable);
    }
    setTransfers((prevTransfers: any) => {
      const updatedTransfers = [...prevTransfers];
      updatedTransfers[index] = {
        ...updatedTransfers[index],
        selectedAmountToTransfer: dollarsToCents(newAmount),
      };
      return updatedTransfers;
    });
  };

  const onWithholdFeeChange = (checked: boolean) => {
    setTransfers((prevTransfers: any) => {
      const updatedTransfers = [...prevTransfers];
      updatedTransfers[index] = {
        ...updatedTransfers[index],
        withholdClearFee: checked,
      };
      return updatedTransfers;
    });
  };

  return (
    <div className="flex flex-column">
      <div className="flex justify-content-between">
        <p className="font-semibold mb-2">Transfer {index + 1}</p>
        {showRemoveButton ? (
          <StyledButton
            data-testid="remove-transfer"
            icon="pi pi-times-circle"
            className="p-button-text p-button-danger p-0 mb-0"
            style={{ alignSelf: "end" }}
            onClick={() => handleRemoveTransfer(index)}
          >
            <p className="small-text">Remove transfer</p>
          </StyledButton>
        ) : null}
      </div>
      <div className="flex flex-column">
        <Label htmlFor="from">From</Label>
        <Dropdown
          options={transferFromOptions}
          value={transfer.selectedPartyToTransferFrom}
          valueTemplate={valueTemplate}
          optionLabel="providerName"
          className="mt-2 mb-4"
          onChange={(e) => onTransferFromChange(e.value)}
          itemTemplate={itemTemplate}
          placeholder="Select party"
        />
        <Label htmlFor="to">To</Label>
        <Dropdown
          options={transferToOptions}
          value={transfer.selectedPartyToTransferTo}
          valueTemplate={valueTemplate}
          optionLabel="name"
          className="mt-2 mb-4"
          onChange={(e) => onTransferToChange(e.value)}
          placeholder="Select party"
          itemTemplate={itemTemplate}
          optionGroupLabel="label"
          optionGroupChildren="items"
          optionGroupTemplate={optionGroupTemplate}
        />
        <Label htmlFor="to">Amount</Label>
        <InputNumber
          value={centsToDollars(transfer.selectedAmountToTransfer)}
          max={
            transfer.selectedPartyToTransferFrom &&
            transfer.selectedPartyToTransferFrom.id
              ? centsToDollars(transfer.selectedPartyToTransferFrom.totalAmount)
              : undefined
          }
          onChange={(e) => onAmountChange(e.value)}
          disabled={!transfer?.selectedPartyToTransferFrom}
          min={0}
          currency="USD"
          locale="en-US"
          mode="currency"
          inputId="amountToTransfer"
          className="mt-2 mb-4"
        />
        {isSuperAdmin &&
          transfer.selectedPartyToTransferFrom?.id === null &&
          transfer.selectedPartyToTransferTo?.id !== null &&
          transfer.selectedPartyToTransferTo?.type === "provider" && (
            <div className="flex align-items-center mb-4">
              <Checkbox
                inputId={`withholdClearFee-${index}`}
                checked={transfer.withholdClearFee || false}
                onChange={(e) => onWithholdFeeChange(e.checked || false)}
              />
              <label htmlFor={`withholdClearFee-${index}`} className="ml-2">
                Withhold clear fee
              </label>
            </div>
          )}
      </div>
    </div>
  );
}
